import { useState } from "react";
import { type ArtifactType } from "~/components/assistant/messages/artifactFormats";
import { Figure, Dataframe, DataValue } from "~/components/assistant/types";
import { trackEvent } from "~/utils/monitoring";

interface ExportArtifactParams {
  artifact: Figure | Dataframe | DataValue;
  artifactType: ArtifactType;
  conversationId: string;
  projectId: number;
  format: string;
  contextId?: string;
}

interface ExportArtifactResponse {
  file_name: string;
  file_path: string;
  file_size_bytes: number;
  s3_key: string;
}

interface ExportResult {
  success: boolean;
  fileName?: string;
  workbenchPath?: string;
  fileIndexed?: boolean;
  error?: string;
}

interface ExportState {
  isExporting: boolean;
  result: ExportResult | null;
}

const FILE_INDEX_POLL_INTERVAL_MS = 1000;
const FILE_INDEX_MAX_ATTEMPTS = 30;

export function useExportArtifact() {
  const [state, setState] = useState<ExportState>({
    isExporting: false,
    result: null,
  });

  const exportArtifact = async ({
    artifact,
    artifactType,
    conversationId,
    projectId,
    format,
    contextId,
  }: ExportArtifactParams): Promise<ExportResult> => {
    setState({ isExporting: true, result: null });

    try {
      const exportResponse = await fetch(
        `/api/ai/v1/${conversationId}/artifacts/${artifact.artifact_id}/export`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            project_id: projectId,
            format,
          }),
        }
      );

      if (!exportResponse.ok) {
        const errorData = await exportResponse.json();
        throw new Error(errorData.detail || "Something went wrong while exporting your file.");
      }

      const { file_name, file_path, file_size_bytes, s3_key }: ExportArtifactResponse =
        await exportResponse.json();

      // Poll the file browser API to verify the file appears in directory listing
      let fileIndexed = false;

      // Construct the directory path and encode each segment
      const directoryKey = `research/projects/${projectId}/exports/`;
      const encodedDirectoryKey = directoryKey
        .split("/")
        .map((segment) => encodeURIComponent(segment))
        .join("/");

      const expectedFileKey = s3_key;

      for (let attempt = 0; attempt < FILE_INDEX_MAX_ATTEMPTS; attempt++) {
        try {
          // Check if the file appears in the exports directory listing
          const checkResponse = await fetch(
            `${import.meta.env.VITE_RWB_API}/file-browser/${encodedDirectoryKey}`
          );

          if (checkResponse.ok) {
            const data = await checkResponse.json();
            // Check if the file appears in the children array
            if (
              data.children &&
              data.children.some((child: { key: string }) => child.key === expectedFileKey)
            ) {
              fileIndexed = true;
              break;
            }
          } else {
            // Log non-200 responses to help diagnose issues
            console.warn("File browser check returned non-OK status:", {
              status: checkResponse.status,
              statusText: checkResponse.statusText,
              attempt: attempt + 1,
              maxAttempts: FILE_INDEX_MAX_ATTEMPTS,
              url: `${import.meta.env.VITE_RWB_API}/file-browser/${encodedDirectoryKey}`,
            });
          }
        } catch (error) {
          // Log errors during file browser check
          console.error("File browser check failed:", {
            error: error instanceof Error ? error.message : String(error),
            attempt: attempt + 1,
            maxAttempts: FILE_INDEX_MAX_ATTEMPTS,
          });
        }

        if (attempt < FILE_INDEX_MAX_ATTEMPTS - 1) {
          await new Promise((resolve) => setTimeout(resolve, FILE_INDEX_POLL_INTERVAL_MS));
        }
      }

      trackEvent("analysis_agent_artifact_export_completed", {
        dataset_id: contextId || "Unknown",
        conversation_id: conversationId,
        artifact_id: artifact.artifact_id,
        artifact_type: artifactType,
        project_id: projectId,
        format,
        file_name: file_name,
        file_size_bytes: file_size_bytes,
        file_indexed: fileIndexed,
      });

      const result: ExportResult = {
        success: true,
        fileName: file_name,
        workbenchPath: file_path,
        fileIndexed,
      };

      setState({ isExporting: false, result });
      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Something went wrong while exporting your file.";

      trackEvent("analysis_agent_artifact_export_failed", {
        dataset_id: contextId || "Unknown",
        conversation_id: conversationId,
        artifact_id: artifact.artifact_id,
        artifact_type: artifactType,
        project_id: projectId,
        format,
        error: errorMessage,
      });

      const result: ExportResult = {
        success: false,
        error: errorMessage,
      };

      setState({ isExporting: false, result });
      return result;
    }
  };

  const reset = () => {
    setState({ isExporting: false, result: null });
  };

  return {
    exportArtifact,
    isExporting: state.isExporting,
    result: state.result,
    reset,
  };
}
